import { useEffect, useRef } from 'react';
import { useRouter } from 'shared-routing';

import { NOT_FOUND_HASH } from '@common/helpers/notFoundRedirect';
import { useTranslations } from '@lang';
import { useToast } from './useToast';

const TOAST_ID = 'not-found-redirect';

// Next keeps its own copy of the url in history.state, so the fragment has to
// leave both or a back/forward pop restores it.
const withoutHash = (value: string) => value.split('#')[0];

export const NotFoundToast = () => {
  const router = useRouter();
  const { t } = useTranslations();
  const { show } = useToast();
  // Keyed on the url so a second dead link announces itself too.
  const announcedUrl = useRef<string | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hash !== `#${NOT_FOUND_HASH}`) return;

    const current = `${window.location.pathname}${window.location.search}`;
    if (announcedUrl.current === current) return;
    announcedUrl.current = current;

    show(t('error.notFoundRedirect'), { tone: 'warning', id: TOAST_ID });

    // The History API, not the router: a shallow replace would fire
    // routeChangeComplete and log a second pageView. Nothing reads the fragment
    // out of router.query, so the router has nothing to correct.
    const state = window.history.state;

    window.history.replaceState(
      state && typeof state === 'object'
        ? {
            ...state,
            ...(typeof state.url === 'string' ? { url: withoutHash(state.url) } : {}),
            ...(typeof state.as === 'string' ? { as: withoutHash(state.as) } : {}),
          }
        : state,
      '',
      current
    );

    // Released once the fragment has gone, so the next dead link may land on this
    // very same listing url and still announce itself.
    if (!window.location.hash) {
      announcedUrl.current = null;
    }
  }, [router.asPath, show, t]);

  return null;
};
